import { ComboboxItem, NativeSelect } from "@mantine/core";

import { spacing } from "#app/theme/v1/spacing";
import { CompanyMachineDto } from "#modules/machines/infra/fetchCompanyMachines.api";
import { UpdateProcessStepRequestDto } from "#modules/processSteps/infra/updateProcessStep.api";
import { useUpdateProcessStep } from "#modules/processSteps/view/useUpdateProcessStep";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { Tooltip } from "#shared/view/designSystem/Tooltip";
import { IndustrialProcessType } from "shared";

type Props = {
  industrialProcess: IndustrialProcessType;
  processStepId: string | undefined;
  companyMachines: CompanyMachineDto[];
  companyMachineId: string | null | undefined;
  currentCompanyMachine?: CompanyMachineDto | null;
  quoteRequestLineId?: string;
};

const NO_MACHINE_ID = "NO_MACHINE_ID";

export const CompanyMachineInput = ({
  industrialProcess,
  processStepId,
  companyMachines,
  companyMachineId,
  currentCompanyMachine,
  quoteRequestLineId,
}: Props) => {
  const { updateProcessStep } = useUpdateProcessStep();
  const analytics = useAnalytics();

  const machinesForProcessStep = companyMachines.filter(
    (machine) => machine.machine.industrialProcess === industrialProcess,
  );
  const isCurrentMachineDeleted =
    !!currentCompanyMachine?.deletedAt && currentCompanyMachine.id === companyMachineId;

  const handleMachineChange = (value: string) => {
    if (!processStepId) {
      return;
    }

    analytics.capture(AnalyticsEvent.DetailsPanelMachineChanged, {
      quote_request_line_id: quoteRequestLineId,
      process_step_id: processStepId,
      industrial_process: industrialProcess,
      machine_id: value === NO_MACHINE_ID ? null : value,
    });

    const machine = machinesForProcessStep.find((m) => m.id === value);

    const payload: UpdateProcessStepRequestDto = {
      companyMachineId: value === NO_MACHINE_ID ? null : value,
      machineHourlyPrice:
        value === NO_MACHINE_ID
          ? undefined
          : machine?.machineHourlyPrice && machine.machineHourlyPrice > 0
            ? machine.machineHourlyPrice
            : undefined,
      description:
        value === NO_MACHINE_ID ? undefined : machine?.processStepDescriptionOverride || undefined,
    };

    if (value !== NO_MACHINE_ID && machine?.processStepDescriptionOverride) {
      payload.description = machine.processStepDescriptionOverride;
    }

    updateProcessStep({
      id: processStepId,
      payload,
    });
  };

  if (machinesForProcessStep.length === 0 && !isCurrentMachineDeleted) {
    return (
      <Tooltip label="Aucune machine disponible pour ce procédé">
        <p style={{ cursor: "help" }}>{"-"}</p>
      </Tooltip>
    );
  }

  const machineOptions: ComboboxItem[] = machinesForProcessStep
    .map((machine) => ({
      value: machine.id,
      label: machine.machine.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const deletedMachineOptions: ComboboxItem[] = isCurrentMachineDeleted
    ? [
        {
          value: currentCompanyMachine.id,
          label: `${currentCompanyMachine.machine.name} (supprimée)`,
          disabled: true,
        },
      ]
    : [];

  return (
    <NativeSelect
      data={[...machineOptions, ...deletedMachineOptions, { value: NO_MACHINE_ID, label: "-" }]}
      value={companyMachineId ?? NO_MACHINE_ID}
      onChange={(e) => handleMachineChange(e.target.value)}
      variant="unstyled"
      size="xs"
      styles={{
        section: { width: spacing.sm },
        input: { paddingRight: spacing.sm },
      }}
    />
  );
};
